/**
 * Wire-format delivery-key 약어 dict.
 *
 * 마스터 데이터의 `rewards` / `retrieveCost` 필드는 시트에 uppercase
 * delivery key (`GOLD`, `RUBY`, `STAGE_TICKET` ...) 로 적힌다.
 * 이 키들은 [packages/shared/src/user-state/types.ts] 의 `DELIVERY_ASSET_MAP` 을
 * 통해 runtime asset id (`gold`, `ruby` 소문자) 로 변환된다.
 *
 * Wire 압축을 위해 직렬화·저장 시점에 짧은 약어로 치환하고
 * 서버 read 시점에 원래 delivery key 로 expand 한다. expand 후 형태는
 * 기존 consumer 코드 (`stage.rewards.GOLD` 등) 가 그대로 동작한다.
 *
 * `DELIVERY_ASSET_MAP` 과 1:1 으로 동기화 — 새 delivery 키가 추가되면
 * 양쪽 모두 갱신. GS Apps Script 에는 같은 dict 가 인라인되어 있으니
 * (`tools/export-sheets.gs` 의 `DELIVERY_CODE_MAP_GS`) 그쪽도 같이 수정.
 *
 * 약어 규칙: 최대 2자, 충돌 없도록 직관적 약자.
 *   GOLD             → g
 *   RUBY             → r
 *   EMERALD          → e
 *   SAPPHIRE         → sp
 *   STAGE_TICKET     → st
 *   LEAGUE_TICKET    → lt
 *   VALHALLA_TICKET  → vt
 *   DEGEN_TICKET     → dt
 *   DEGEN_SILVER_TICKET → ds
 *   DEGEN_GOLDEN_TICKET → dg
 *   FOLKVANGR_TICKET → ft
 *   GOLDEN_BLESSING  → gb
 *   THOR_WRATH       → tw
 *   BLESSING_OF_FREIA→ bf
 *   UPGRADE_RESET_TICKET→ ur
 *   QUICK_GAIN_TICKET→ qg
 *   BREAKTHROUGH_STONE_PIECE→ bp
 *   BREAKTHROUGH_STONE_LOW → bl
 *   BREAKTHROUGH_STONE_MID → bm
 *   BREAKTHROUGH_STONE_HIGH→ bh
 *   SKIN_SHARD             → sh
 */
export const DELIVERY_CODE_MAP: Record<string, string> = {
  GOLD: 'g',
  RUBY: 'r',
  EMERALD: 'e',
  SAPPHIRE: 'sp',
  STAGE_TICKET: 'st',
  LEAGUE_TICKET: 'lt',
  VALHALLA_TICKET: 'vt',
  DEGEN_TICKET: 'dt',
  DEGEN_SILVER_TICKET: 'ds',
  DEGEN_GOLDEN_TICKET: 'dg',
  FOLKVANGR_TICKET: 'ft',
  GOLDEN_BLESSING: 'gb',
  THOR_WRATH: 'tw',
  BLESSING_OF_FREIA: 'bf',
  UPGRADE_RESET_TICKET: 'ur',
  QUICK_GAIN_TICKET: 'qg',
  BREAKTHROUGH_STONE_PIECE: 'bp',
  BREAKTHROUGH_STONE_LOW: 'bl',
  BREAKTHROUGH_STONE_MID: 'bm',
  BREAKTHROUGH_STONE_HIGH: 'bh',
  SKIN_SHARD: 'sh',
};

export const DELIVERY_CODE_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(DELIVERY_CODE_MAP).map(([k, v]) => [v, k])
);

/**
 * `{ GOLD: 100, RUBY: 10 }` → `{ g: 100, r: 10 }`.
 * dict 에 없는 키는 그대로 통과 (idempotent + forward-compat).
 */
export function abbrevDeliveryRecord<T>(
  rec: Record<string, T> | null | undefined
): Record<string, T> | null | undefined {
  if (rec == null) return rec;
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[DELIVERY_CODE_MAP[k] ?? k] = v;
  }
  return out;
}

/**
 * `{ g: 100, r: 10 }` → `{ GOLD: 100, RUBY: 10 }`.
 * 약어 dict reverse 에 없는 키는 그대로 통과.
 *
 * Backward compat: state 에 expansion 안 된 데이터 (예: 직전 reseed 가
 * 약어 없이 들어간 상태) 가 남아 있을 때 verbose key 는 변경 없이 통과.
 */
export function expandDeliveryRecord<T>(
  rec: Record<string, T> | null | undefined
): Record<string, T> | null | undefined {
  if (rec == null) return rec;
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[DELIVERY_CODE_REVERSE[k] ?? k] = v;
  }
  return out;
}
